import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { toast } from "@devhop/ui";
import type { OrderExportColumnKey } from "@/modules/orders/domain/contracts";
import type {
  OffsetPageQueryDTO,
  SortFieldDTO,
} from "@/shared/contracts/base";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useCallback, useRef, useState } from "react";
import { ordersApi } from "../api/client";
import { useOrdersQuery } from "../api/queries";
import {
  buildOrderExportCsv,
  downloadOrdersCsv,
  ordersExportFilename,
} from "../lib/ordersCsv";
import { OrderSummaryCard } from "../ui/OrderSummaryCard";
import { OrdersFilter } from "../ui/OrdersFilter";
import { OrdersTable } from "../ui/OrdersTable";
import { OrdersToolbar } from "../ui/OrdersToolbar";

const DEFAULT_ORDER_SORT: SortFieldDTO[] = [
  { field: "createdAt", dir: "desc" },
];

export function OrdersPage() {
  const nav = useNavigate({ from: "/app/orders" });
  const search: OffsetPageQueryDTO = useSearch({ from: "/app/orders" });
  const sort =
    search.sort && search.sort.length > 0 ? search.sort : DEFAULT_ORDER_SORT;
  const list = useOrdersQuery({
    offset: search.offset,
    limit: search.limit,
    sort,
    filters: search.filters,
  });

  const [exportLoading, setExportLoading] = useState(false);
  const exportInFlight = useRef(false);
  const totalFiltered = list.data?.meta?.total ?? 0;

  const handleExportCsv = useCallback(
    async (columns: OrderExportColumnKey[]) => {
      if (totalFiltered <= 0 || exportInFlight.current) return;
      exportInFlight.current = true;
      setExportLoading(true);
      try {
        const { lines } = await ordersApi.exportLines({
          sort,
          filters: search.filters,
          columns,
          limit: 50_000,
        });
        const csv = buildOrderExportCsv(lines, columns);
        downloadOrdersCsv(csv, ordersExportFilename());
        toast.success("ສົ່ງອອກແລ້ວ", {
          description: `${lines.length} ແຖວ`,
        });
      } catch (e: unknown) {
        toast.error("ສົ່ງອອກລົ້ມເຫຼວ", {
          description: e instanceof Error ? e.message : undefined,
        });
      } finally {
        exportInFlight.current = false;
        setExportLoading(false);
      }
    },
    [search.filters, sort, totalFiltered],
  );

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ຄຳສັ່ງຊື້" },
          ]}
        />
      </Header>
      <Main>
        <OrdersToolbar
          onExportCsv={handleExportCsv}
          exportCsvLoading={exportLoading}
          canExportCsv={
            totalFiltered > 0 && !list.isLoading && !list.isFetching
          }
        />
        <div className="mb-4">
          <OrderSummaryCard />
        </div>
        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <OrdersFilter />
          <OrdersTable
            data={list.data?.data ?? []}
            isLoading={list.isLoading}
            offset={search.offset ?? 0}
            limit={search.limit ?? 20}
            totalCount={list.data?.meta?.total ?? 0}
            sortBy={sort[0]?.field}
            sortOrder={sort[0]?.dir}
            onPaginationChange={(offset, limit) =>
              nav({ search: { ...search, offset, limit } })
            }
            onSortingChange={(id, desc) =>
              nav({
                search: {
                  ...search,
                  sort: [{ field: id, dir: desc ? "desc" : "asc" }],
                },
              })
            }
            onView={(row) =>
              nav({ to: "/app/orders/$id", params: { id: row.id } })
            }
          />
        </div>
      </Main>
    </>
  );
}
